import React from 'react';

export interface ProgressBarProps {
  /** Completed count. */
  value: number;
  /** Total count. */
  max: number;
  /** Optional left-side label above the track. */
  label?: string;
  /** Show the "value/max lokale" count on the right. Default true. */
  showCount?: boolean;
  /** Replaces the default "value/max lokale" count text (e.g. "zrobione 22 z 27 lokali"). */
  countText?: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * ProgressBar — building completion ("3/4 lokale"). Flat track + fill.
 * Neutral by default; turns green only when complete.
 */
export function ProgressBar({
  value,
  max,
  label,
  showCount = true,
  countText,
  style,
}: ProgressBarProps) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const complete = max > 0 && value >= max;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', ...style }}>
      {(label || showCount) && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-label)',
          color: 'var(--text-secondary)',
        }}>
          <span>{label}</span>
          {showCount && (
            <span style={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 'var(--weight-semibold)',
              color: complete ? 'var(--status-ok)' : 'var(--text-primary)',
            }}>
              {countText ?? `${value}/${max} lokale`}
            </span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        style={{
          height: 10,
          borderRadius: 'var(--radius-pill)',
          background: 'var(--gray-200)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          width: pct + '%',
          height: '100%',
          borderRadius: 'var(--radius-pill)',
          background: complete ? 'var(--status-ok-bar)' : 'var(--action-primary)',
          transition: 'width .2s ease',
        }} />
      </div>
    </div>
  );
}
